/**
 * ClassesTabContent
 * Your classes + syllabus management in one integrated view
 * Uses URL-based routing: /dashboard/classes, /dashboard/classes/syllabi/list, /dashboard/classes/syllabi/view/:id
 */

import { useState } from 'react';
import { Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { YourClasses } from '../YourClasses';
import { SyllabusUploader, SyllabusViewer } from '@/components/syllabus';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Upload, Sparkles, Calendar } from 'lucide-react';
import type { ParsedSyllabusData } from '@/types/syllabus';
import { useToast } from '@/hooks/use-toast';
import { useSyllabi, syllabusKeys } from '@/hooks/use-syllabus';
import { useGenerateTasksFromSyllabus, useTasksBySyllabus } from '@/hooks/use-tasks';
import { useProfileCourses, useAddCourse } from '@/hooks/use-learning-profile';
import { useActiveStudyPlan } from '@/hooks/use-study-plan';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { LEGAL_SUBJECTS } from '@/types/learning-profile';
import { CreateStudyPlanWizard } from '@/components/study-plan/CreateStudyPlanWizard';
import { normalizeSubject } from '@/lib/subject-mapping';

/**
 * Try to match a course name to one of the official legal subjects
 * Uses the unified subject-mapping system with additional handling for
 * numbered courses (e.g., "Contracts I", "Torts II")
 *
 * Returns the subject slug (e.g., 'contracts') if matched, null otherwise
 */
function matchCourseToSubject(courseName: string | undefined | null): string | null {
  if (!courseName) return null;

  const normalized = courseName.toLowerCase().trim();

  // First, try direct normalization via unified subject mapping
  const directMatch = normalizeSubject(courseName);
  // normalizeSubject returns the input if not recognized, so check if it's a known slug
  if (directMatch && LEGAL_SUBJECTS.some(s => s.value === directMatch)) {
    return directMatch;
  }

  // Strip common suffixes like "I", "II", "1", "2" and try again
  const withoutSuffix = normalized
    .replace(/\s+(i{1,3}|iv|v|1|2|3)$/i, '') // Roman numerals and numbers at end
    .trim();

  if (withoutSuffix !== normalized) {
    const suffixStrippedMatch = normalizeSubject(withoutSuffix);
    if (suffixStrippedMatch && LEGAL_SUBJECTS.some(s => s.value === suffixStrippedMatch)) {
      return suffixStrippedMatch;
    }
  }

  // Check if any LEGAL_SUBJECTS label is contained in the course name
  for (const subject of LEGAL_SUBJECTS) {
    const label = subject.label.toLowerCase();
    if (normalized.includes(label)) {
      return subject.value;
    }
  }

  return null;
}

interface ClassesTabContentProps {
  className?: string;
}

export function ClassesTabContent({ className }: ClassesTabContentProps) {
  return (
    <div className={cn('space-y-6', className)}>
      <Routes>
        {/* Default: Classes view with syllabus uploader */}
        <Route index element={<ClassesMainView />} />
        {/* Syllabus detail */}
        <Route path="syllabi/view/:syllabusId" element={<SyllabusDetailView />} />
        {/* Redirect old routes to main classes page */}
        <Route path="syllabi/list" element={<Navigate to="/dashboard/classes" replace />} />
        <Route path="syllabi" element={<Navigate to="/dashboard/classes" replace />} />
      </Routes>
    </div>
  );
}

/**
 * Main view - Your Classes + Syllabus Upload
 */
function ClassesMainView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { data: existingCourses = [] } = useProfileCourses();
  const { data: activePlan, isLoading: planLoading } = useActiveStudyPlan();
  const addCourse = useAddCourse();
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  // Show CTA only when user has classes but no active plan
  const showGeneratePlanCTA = existingCourses.length > 0 && !activePlan && !planLoading;

  const handleUploadSuccess = async (syllabusId: string, parsedData?: ParsedSyllabusData) => {
    // Invalidate syllabi cache so the list updates immediately
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: syllabusKeys.list(user.id) });
    }

    // Try to auto-match and add course
    const matchedSubject = matchCourseToSubject(parsedData?.course_name);
    const alreadyHasCourse = existingCourses.some((c) => c.subject === matchedSubject);

    if (matchedSubject && !alreadyHasCourse) {
      try {
        await addCourse.mutateAsync({ subject: matchedSubject });
        const subjectLabel = LEGAL_SUBJECTS.find((s) => s.value === matchedSubject)?.label || matchedSubject;
        toast({
          title: 'Class added!',
          description: `${subjectLabel} was automatically added to your classes.`,
        });
      } catch {
        // Silently fail if course can't be added - user can add manually
      }
    }

    toast({
      title: 'Syllabus uploaded!',
      description: parsedData?.course_name
        ? `Successfully analyzed ${parsedData.course_name}`
        : 'Your syllabus has been uploaded and analyzed.',
    });

    // Navigate to view the uploaded syllabus
    navigate(`/dashboard/classes/syllabi/view/${syllabusId}`);
  };

  const handleUploadError = (error: string) => {
    toast({
      title: 'Upload failed',
      description: error,
      variant: 'destructive',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Title - Gold Standard Pattern */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Your Classes</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Manage your classes and syllabi
        </p>
      </div>

      {/* Your Classes Section */}
      <YourClasses />

      {/* Generate Study Plan CTA - shown only when classes exist but no plan */}
      {showGeneratePlanCTA && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base text-foreground">
                  Ready to create your study plan?
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  After you've entered all your classes and syllabi, generate a personalized attack plan
                  tailored to your exam schedule and learning goals.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={() => setShowCreateWizard(true)}
                  className="w-full sm:w-auto"
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Generate Plan
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Study Plan Sheet */}
      <Sheet open={showCreateWizard} onOpenChange={setShowCreateWizard}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle>Create Attack Plan</SheetTitle>
            <SheetDescription>
              Set up your personalized study plan based on your subjects and schedule.
            </SheetDescription>
          </SheetHeader>
          <CreateStudyPlanWizard
            onComplete={() => {
              setShowCreateWizard(false);
              toast({
                title: 'Attack plan created!',
                description: 'Your personalized study plan is ready.',
              });
            }}
            onCancel={() => setShowCreateWizard(false)}
            onUploadSyllabi={() => {
              setShowCreateWizard(false);
              // Scroll to syllabus upload section after sheet closes
              setTimeout(() => {
                document.getElementById('syllabus-upload-section')?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start'
                });
              }, 300);
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Syllabus Upload Section */}
      <Card id="syllabus-upload-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-4 w-4 text-primary" />
            Upload Syllabus
          </CardTitle>
          <CardDescription>
            Add a syllabus to auto-generate tasks and track deadlines
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SyllabusUploader
            onSuccess={handleUploadSuccess}
            onError={handleUploadError}
          />
        </CardContent>
      </Card>

      {/* How It Works */}
      <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg p-4">
        <h4 className="font-medium text-foreground mb-2">How it works:</h4>
        <ol className="list-decimal list-inside space-y-1">
          <li>Upload your syllabus (PDF or image)</li>
          <li>Our AI extracts key dates, exams, and assignments</li>
          <li>We create tasks automatically linked to your classes</li>
        </ol>
      </div>
    </div>
  );
}

/**
 * Syllabus detail view
 */
function SyllabusDetailView() {
  const { syllabusId } = useParams<{ syllabusId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: syllabi = [], isLoading } = useSyllabi();
  const generateTasks = useGenerateTasksFromSyllabus();
  const { data: syllabusTasks = [] } = useTasksBySyllabus(syllabusId);

  // Find the syllabus by ID
  const syllabus = syllabi.find((s) => s.id === syllabusId);

  const handleBack = () => {
    navigate('/dashboard/classes');
  };

  const handleGenerateStudyPlan = () => {
    if (syllabus) {
      navigate(`/study-plan/create?syllabusId=${syllabus.id}`);
    }
  };

  const handleGenerateTasks = async () => {
    if (!syllabus || !syllabus.parsed_data) return;

    const parsedData = syllabus.parsed_data;
    const courseName = syllabus.course_name_extracted || syllabus.file_name || 'Unknown Course';

    try {
      const result = await generateTasks.mutateAsync({
        syllabusId: syllabus.id,
        courseName,
        exams: parsedData.exams || [],
        assignments: parsedData.assignments || [],
      });

      const totalTasks = result.examTasks.length + result.assignmentTasks.length;

      if (totalTasks > 0) {
        toast({
          title: 'Tasks generated!',
          description: `Created ${totalTasks} task${totalTasks === 1 ? '' : 's'} from your syllabus.`,
        });
      } else {
        toast({
          title: 'No new tasks',
          description: 'All tasks from this syllabus have already been created, or no dated items were found.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error generating tasks',
        description: error instanceof Error ? error.message : 'Something went wrong',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <div className="animate-pulse h-64 bg-muted rounded-lg" />;
  }

  if (!syllabus) {
    return <Navigate to="/dashboard/classes" replace />;
  }

  const hasExamsOrAssignments =
    (syllabus.parsed_data?.exams?.length ?? 0) > 0 ||
    (syllabus.parsed_data?.assignments?.length ?? 0) > 0;

  return (
    <div className="space-y-4">
      {/* Syllabus Viewer - handles its own header */}
      <SyllabusViewer
        syllabus={syllabus}
        onBack={handleBack}
        onGenerateStudyPlan={handleGenerateStudyPlan}
        onGenerateTasks={handleGenerateTasks}
        isGeneratingTasks={generateTasks.isPending}
        existingTasksCount={syllabusTasks.length}
        hasExamsOrAssignments={hasExamsOrAssignments}
      />
    </div>
  );
}

export default ClassesTabContent;
